import React, { useState, useEffect } from 'react';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import Container from '@mui/material/Container';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Alert from '@mui/material/Alert';
import Snackbar from '@mui/material/Snackbar';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';

import ImageUploader from './components/ImageUploader';
import QuestionsList from './components/QuestionsList';
import ResultsTable from './components/ResultsTable';
import SolutionsTable from './components/SolutionsTable';
import { apiService } from './api/apiService';
import { openaiService } from './api/openaiService';
import { anthropicService } from './api/anthropicService';
import { geminiService } from './api/geminiService';
import { resultsService } from './api/resultsService';
import config from './config';

const theme = createTheme({
  palette: {
    primary: {
      main: '#1a73e8',
    },
    secondary: {
      main: '#f50057',
    },
  },
});

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ pt: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

function App() {
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [processingQuestions, setProcessingQuestions] = useState([]);
  const [processingOpenAIQuestions, setProcessingOpenAIQuestions] = useState([]);
  const [processingAnthropicQuestions, setProcessingAnthropicQuestions] = useState([]);
  const [processedImages, setProcessedImages] = useState([]);
  const [apiKeyDialogOpen, setApiKeyDialogOpen] = useState(false);
  const [apiKey, setApiKey] = useState(config.geminiApiKey);
  const [anthropicApiKey, setAnthropicApiKey] = useState(config.anthropicApiKey);
  const [notification, setNotification] = useState({
    open: false,
    message: '',
    severity: 'info'
  });
  const [tabValue, setTabValue] = useState(0);

  // Initialize the results table in local storage when the app loads
  useEffect(() => {
    // Check if neetResults exists in localStorage
    if (!localStorage.getItem('neetResults')) {
      // Initialize with empty array if it doesn't exist
      localStorage.setItem('neetResults', JSON.stringify([]));
    }
  }, []);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleApiKeySubmit = () => {
    config.geminiApiKey = apiKey;
    config.anthropicApiKey = anthropicApiKey;
    setApiKeyDialogOpen(false);
    setNotification({
      open: true,
      message: 'API Keys configured successfully!',
      severity: 'success'
    });
  };

  const handleImagesProcessed = async (images) => {
    setLoading(true);
    setProcessedImages(images);
    
    try {
      // Extract questions from images
      const extractedQuestions = await apiService.extractQuestions(images);
      setQuestions(extractedQuestions);
      
      if (extractedQuestions.length === 0) {
        setNotification({
          open: true,
          message: 'No questions were extracted from the images. Try with different images.',
          severity: 'warning'
        });
      } else {
        setNotification({
          open: true,
          message: `Successfully extracted ${extractedQuestions.length} questions!`,
          severity: 'success'
        });
      }
    } catch (error) {
      console.error("Error processing images:", error);
      setNotification({
        open: true,
        message: `Error: ${error.message}`,
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSolveQuestion = async (questionId) => {
    setProcessingQuestions(prev => [...prev, questionId]);
    
    try {
      const questionToSolve = questions.find(q => q.id === questionId);
      if (!questionToSolve || questionToSolve.solution) {
        setProcessingQuestions(prev => prev.filter(id => id !== questionId));
        return;
      }
      
      const solutionData = await apiService.generateSolution(questionToSolve);
      
      // Update just this question with the solution and correct answer
      setQuestions(prevQuestions => 
        prevQuestions.map(q => 
          q.id === questionId ? { 
            ...q, 
            solution: solutionData.solution,
            correctAnswer: solutionData.correctAnswer,
            geminiAnswer: solutionData.correctAnswer,
            geminiSolution: solutionData.solution
          } : q
        )
      );
      
      // Update both answer and solution in results table
      resultsService.updateModelAnswerAndSolution(questionId, 'gemini', solutionData.correctAnswer, solutionData.solution);
      
      setNotification({
        open: true,
        message: `Solution generated for question ${questionToSolve.questionNumber}!`,
        severity: 'success'
      });
    } catch (error) {
      console.error(`Error solving question ${questionId}:`, error);
      setNotification({
        open: true,
        message: `Error generating solution: ${error.message}`,
        severity: 'error'
      });
    } finally {
      setProcessingQuestions(prev => prev.filter(id => id !== questionId));
    }
  };

  const handleSolveWithOpenAI = async (questionId) => {
    setProcessingOpenAIQuestions(prev => [...prev, questionId]);
    
    try {
      const questionToSolve = questions.find(q => q.id === questionId);
      if (!questionToSolve) {
        setProcessingOpenAIQuestions(prev => prev.filter(id => id !== questionId));
        return;
      }
      
      const solutionData = await openaiService.generateSolution(questionToSolve);
      
      // Update the question with the OpenAI solution
      setQuestions(prevQuestions => 
        prevQuestions.map(q => 
          q.id === questionId ? { 
            ...q, 
            openaiSolution: solutionData.solution,
            openaiAnswer: solutionData.correctAnswer,
            correctAnswer: q.correctAnswer || solutionData.correctAnswer
          } : q
        )
      );
      
      // Update both answer and solution in results table
      resultsService.updateModelAnswerAndSolution(questionId, 'openai', solutionData.correctAnswer, solutionData.solution);
      
      setNotification({
        open: true,
        message: `OpenAI solution generated for question ${questionToSolve.questionNumber}!`,
        severity: 'success'
      });
    } catch (error) {
      console.error(`Error solving question with OpenAI ${questionId}:`, error);
      setNotification({
        open: true,
        message: `Error generating OpenAI solution: ${error.message}`,
        severity: 'error'
      });
    } finally {
      setProcessingOpenAIQuestions(prev => prev.filter(id => id !== questionId));
    }
  };

  const handleSolveWithAnthropic = async (questionId) => {
    setProcessingAnthropicQuestions(prev => [...prev, questionId]);
    
    try {
      const questionToSolve = questions.find(q => q.id === questionId);
      if (!questionToSolve) {
        setProcessingAnthropicQuestions(prev => prev.filter(id => id !== questionId));
        return;
      }
      
      const solutionData = await anthropicService.generateSolution(questionToSolve);
      
      // Update the question with the Anthropic solution
      setQuestions(prevQuestions => 
        prevQuestions.map(q => 
          q.id === questionId ? { 
            ...q, 
            anthropicSolution: solutionData.solution,
            anthropicAnswer: solutionData.correctAnswer,
            correctAnswer: q.correctAnswer || solutionData.correctAnswer
          } : q
        )
      );
      
      // Update both answer and solution in results table
      resultsService.updateModelAnswerAndSolution(questionId, 'anthropic', solutionData.correctAnswer, solutionData.solution);
      
      setNotification({
        open: true,
        message: `Anthropic solution generated for question ${questionToSolve.questionNumber}!`,
        severity: 'success'
      });
    } catch (error) {
      console.error(`Error solving question with Anthropic ${questionId}:`, error);
      setNotification({
        open: true,
        message: `Error generating Anthropic solution: ${error.message}`,
        severity: 'error'
      });
    } finally {
      setProcessingAnthropicQuestions(prev => prev.filter(id => id !== questionId));
    }
  };

  const handleSolveQuestionWithImage = async (questionId) => {
    setProcessingQuestions(prev => [...prev, questionId]);
    
    try {
      const questionToSolve = questions.find(q => q.id === questionId);
      if (!questionToSolve || !questionToSolve.imageSource) {
        setProcessingQuestions(prev => prev.filter(id => id !== questionId));
        return;
      }
      
      // Get the original image file from the processedImages array using sourceImageName
      const imageFile = processedImages.find(img => img.name === questionToSolve.sourceImageName);
      if (!imageFile) {
        throw new Error('Source image not found');
      }
      
      const solutionData = await geminiService.generateSolutionFromImage(questionToSolve, imageFile);
      
      // Update just this question with the solution and correct answer
      setQuestions(prevQuestions => 
        prevQuestions.map(q => 
          q.id === questionId ? { 
            ...q, 
            solution: solutionData.solution,
            correctAnswer: solutionData.correctAnswer,
            geminiAnswer: solutionData.correctAnswer,
            geminiSolution: solutionData.solution
          } : q
        )
      );
      
      // Update both answer and solution in results table
      resultsService.updateModelAnswerAndSolution(questionId, 'gemini', solutionData.correctAnswer, solutionData.solution);
      
      setNotification({
        open: true,
        message: `Vision solution generated for question ${questionToSolve.questionNumber}!`,
        severity: 'success'
      });
    } catch (error) {
      console.error(`Error solving question with Gemini Vision ${questionId}:`, error);
      setNotification({
        open: true,
        message: `Error generating vision solution: ${error.message}`,
        severity: 'error'
      });
    } finally {
      setProcessingQuestions(prev => prev.filter(id => id !== questionId));
    }
  };

  const handleSolveWithOpenAIVision = async (questionId) => {
    setProcessingOpenAIQuestions(prev => [...prev, questionId]);
    
    try {
      const questionToSolve = questions.find(q => q.id === questionId);
      if (!questionToSolve || !questionToSolve.imageSource) {
        setProcessingOpenAIQuestions(prev => prev.filter(id => id !== questionId));
        return;
      }
      
      // Get the original image file from the processedImages array using sourceImageName
      const imageFile = processedImages.find(img => img.name === questionToSolve.sourceImageName);
      if (!imageFile) {
        throw new Error('Source image not found');
      }
      
      const solutionData = await openaiService.generateSolutionFromImage(questionToSolve, imageFile);
      
      // Update the question with the OpenAI solution
      setQuestions(prevQuestions => 
        prevQuestions.map(q => 
          q.id === questionId ? { 
            ...q, 
            openaiSolution: solutionData.solution,
            openaiAnswer: solutionData.correctAnswer,
            correctAnswer: q.correctAnswer || solutionData.correctAnswer
          } : q
        )
      );
      
      // Update both answer and solution in results table
      resultsService.updateModelAnswerAndSolution(questionId, 'openai', solutionData.correctAnswer, solutionData.solution);
      
      setNotification({
        open: true,
        message: `Vision solution generated for question ${questionToSolve.questionNumber}!`,
        severity: 'success'
      });
    } catch (error) {
      console.error(`Error solving question with OpenAI Vision ${questionId}:`, error);
      setNotification({
        open: true,
        message: `Error generating vision solution: ${error.message}`,
        severity: 'error'
      });
    } finally {
      setProcessingOpenAIQuestions(prev => prev.filter(id => id !== questionId));
    }
  };

  const handleSolveAll = async () => {
    // Get all questions that don't have Gemini solutions
    const questionsToSolve = questions.filter(q => !q.geminiAnswer);
    
    if (questionsToSolve.length === 0) {
      setNotification({
        open: true,
        message: 'All questions already have Gemini solutions!',
        severity: 'info'
      });
      return;
    }
    
    // Mark all these questions as processing
    setProcessingQuestions(questionsToSolve.map(q => q.id));
    
    // Process each question sequentially, but update UI after each one
    for (const question of questionsToSolve) {
      try {
        const solutionData = await apiService.generateSolution(question);
        
        // Update this question with the Gemini solution and correct answer
        setQuestions(prevQuestions => 
          prevQuestions.map(q => 
            q.id === question.id ? { 
              ...q, 
              geminiSolution: solutionData.solution,
              correctAnswer: solutionData.correctAnswer,
              geminiAnswer: solutionData.correctAnswer
            } : q
          )
        );
        
        // Update both answer and solution in results table
        resultsService.updateModelAnswerAndSolution(question.id, 'gemini', solutionData.correctAnswer, solutionData.solution);
        
        // Remove this question from processing list
        setProcessingQuestions(prev => prev.filter(id => id !== question.id));
        
      } catch (error) {
        console.error(`Error solving question ${question.id}:`, error);
        
        // Show notification but continue with other questions
        setNotification({
          open: true,
          message: `Error with question ${question.questionNumber}: ${error.message}`,
          severity: 'error'
        });
        
        // Remove this question from processing list
        setProcessingQuestions(prev => prev.filter(id => id !== question.id));
      }
    }
    
    setNotification({
      open: true,
      message: 'Finished processing all questions with Gemini!',
      severity: 'success'
    });
  };

  const handleSolveAllWithOpenAI = async () => {
    // Get all questions that don't have OpenAI solutions
    const questionsToSolve = questions.filter(q => !q.openaiAnswer);
    
    if (questionsToSolve.length === 0) {
      setNotification({
        open: true,
        message: 'All questions already have OpenAI solutions!',
        severity: 'info'
      });
      return;
    }
    
    // Mark all these questions as processing with OpenAI
    setProcessingOpenAIQuestions(questionsToSolve.map(q => q.id));
    
    // Process each question sequentially, but update UI after each one
    for (const question of questionsToSolve) {
      try {
        const solutionData = await openaiService.generateSolution(question);
        
        // Update this question with the OpenAI solution and correct answer
        setQuestions(prevQuestions => 
          prevQuestions.map(q => 
            q.id === question.id ? { 
              ...q, 
              openaiSolution: solutionData.solution, // Store OpenAI solution separately
              openaiAnswer: solutionData.correctAnswer,
              correctAnswer: q.correctAnswer || solutionData.correctAnswer // Keep existing correct answer if present
            } : q
          )
        );
        
        // Also update the results table
        if (solutionData.correctAnswer) {
          resultsService.updateModelAnswer(question.id, 'openai', solutionData.correctAnswer);
        }
        
        // Remove this question from OpenAI processing list
        setProcessingOpenAIQuestions(prev => prev.filter(id => id !== question.id));
        
      } catch (error) {
        console.error(`Error solving question with OpenAI ${question.id}:`, error);
        
        // Show notification but continue with other questions
        setNotification({
          open: true,
          message: `Error with OpenAI on question ${question.questionNumber}: ${error.message}`,
          severity: 'error'
        });
        
        // Remove this question from OpenAI processing list
        setProcessingOpenAIQuestions(prev => prev.filter(id => id !== question.id));
      }
    }
    
    setNotification({
      open: true,
      message: 'Finished processing all questions with OpenAI!',
      severity: 'success'
    });
  };

  const handleSolveAllWithAnthropic = async () => {
    // Get all questions that don't have Anthropic solutions
    const questionsToSolve = questions.filter(q => !q.anthropicAnswer);
    
    if (questionsToSolve.length === 0) {
      setNotification({
        open: true,
        message: 'All questions already have Anthropic solutions!',
        severity: 'info'
      });
      return;
    }
    
    // Mark all these questions as processing with Anthropic
    setProcessingAnthropicQuestions(questionsToSolve.map(q => q.id));
    
    // Process each question sequentially, but update UI after each one
    for (const question of questionsToSolve) {
      try {
        const solutionData = await anthropicService.generateSolution(question);
        
        // Update this question with the Anthropic solution and correct answer
        setQuestions(prevQuestions => 
          prevQuestions.map(q => 
            q.id === question.id ? { 
              ...q, 
              anthropicSolution: solutionData.solution,
              anthropicAnswer: solutionData.correctAnswer,
              correctAnswer: q.correctAnswer || solutionData.correctAnswer
            } : q
          )
        );
        
        // Also update the results table
        if (solutionData.correctAnswer) {
          resultsService.updateModelAnswer(question.id, 'anthropic', solutionData.correctAnswer);
        }
        
        // Remove this question from Anthropic processing list
        setProcessingAnthropicQuestions(prev => prev.filter(id => id !== question.id));
        
      } catch (error) {
        console.error(`Error solving question with Anthropic ${question.id}:`, error);
        
        // Show notification but continue with other questions
        setNotification({
          open: true,
          message: `Error with Anthropic on question ${question.questionNumber}: ${error.message}`,
          severity: 'error'
        });
        
        // Remove this question from Anthropic processing list
        setProcessingAnthropicQuestions(prev => prev.filter(id => id !== question.id));
      }
    }
    
    setNotification({
      open: true,
      message: 'Finished processing all questions with Anthropic!',
      severity: 'success'
    });
  };

  const handleCloseNotification = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setNotification({ ...notification, open: false });
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AppBar position="static">
        <Toolbar>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            NEET Paper Solver
          </Typography>
          <Button 
            color="inherit" 
            onClick={() => setApiKeyDialogOpen(true)}
          >
            Configure API Key
          </Button>
        </Toolbar>
      </AppBar>
      <Container maxWidth="lg">
        <Box sx={{ my: 4 }}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs value={tabValue} onChange={handleTabChange} aria-label="basic tabs example">
              <Tab label="Question Solver" />
              <Tab label="AI Model Comparison" />
              <Tab label="Detailed Solutions" />
            </Tabs>
          </Box>
          
          <TabPanel value={tabValue} index={0}>
            <ImageUploader 
              onImagesProcessed={handleImagesProcessed}
              loading={loading}
            />
            
            {questions.length > 0 && (
              <QuestionsList 
                questions={questions}
                onSolveQuestion={handleSolveQuestion}
                onSolveWithOpenAI={handleSolveWithOpenAI}
                onSolveWithAnthropic={handleSolveWithAnthropic}
                onSolveAll={handleSolveAll}
                onSolveAllWithOpenAI={handleSolveAllWithOpenAI}
                onSolveAllWithAnthropic={handleSolveAllWithAnthropic}
                onSolveQuestionWithImage={handleSolveQuestionWithImage}
                onSolveWithOpenAIVision={handleSolveWithOpenAIVision}
                processingQuestions={processingQuestions}
                processingOpenAIQuestions={processingOpenAIQuestions}
                processingAnthropicQuestions={processingAnthropicQuestions}
              />
            )}
          </TabPanel>
          
          <TabPanel value={tabValue} index={1}>
            <ResultsTable />
          </TabPanel>

          <TabPanel value={tabValue} index={2}>
            <SolutionsTable />
          </TabPanel>
        </Box>
      </Container>
      
      {/* API Keys Dialog */}
      <Dialog open={apiKeyDialogOpen} onClose={() => setApiKeyDialogOpen(false)}>
        <DialogTitle>Configure API Keys</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Please enter your API keys to use the services for question extraction and solution generation.
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            id="geminiApiKey"
            label="Gemini API Key"
            type="password"
            fullWidth
            variant="outlined"
            value={apiKey}
            onChange={(e) => setApiKey(e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            id="anthropicApiKey"
            label="Anthropic API Key"
            type="password"
            fullWidth
            variant="outlined"
            value={anthropicApiKey}
            onChange={(e) => setAnthropicApiKey(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApiKeyDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleApiKeySubmit} variant="contained" color="primary">
            Save API Keys
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Notification Snackbar */}
      <Snackbar 
        open={notification.open} 
        autoHideDuration={6000} 
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert 
          onClose={handleCloseNotification} 
          severity={notification.severity} 
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </ThemeProvider>
  );
}

export default App;